'use client';

import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { Tooltip } from '@chakra-ui/react';
import { CheckCheck, Copy, ThumbsDown, ThumbsUp } from 'lucide-react';

import { Markdown, Message, MessageActions } from '@/components/common';
import { AgentThinkingOrb, IconButton } from '@/components/core';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { useIsTouchDevice } from '@/hooks/useIsTouchDevice';
import { useMessageFeedback } from '@/hooks/useMessageFeedback';
import { useThrottledContent } from '@/hooks/useThrottledContent';
import { cn } from '@/lib/utils';
import {
  CHAT_MESSAGE_RATING,
  ChatMessage,
  LibraryReference,
} from '@/types/chat';

import {
  buildCitedAnswer,
  canRenderAsCitedAnswer,
  linkLibraryCitations,
} from '../utils/citedAnswer';
import {
  libraryReferencesFor,
  mergeReferences,
} from '../utils/libraryDocuments';
import { shouldRenderLiveMarkdown } from '../utils/liveMarkdown';
import { AgentThinkingTrace } from './AgentThinkingTrace';
import { CitedAnswerMessage } from './CitedAnswerMessage';
import { ConnieWorkingPanel } from './ConnieWorkingPanel';
import { LastBotMessage } from './LastBotMessage';
import { MessageFeedbackPopover } from './MessageFeedback';
import { ToolCallChips } from './ToolCallChips';
import {
  WorksheetPhotoProvider,
  WorksheetPhotoStrip,
} from './WorksheetPhotoStrip';

interface Props {
  showMessageActions?: boolean;
  showFeedback?: boolean;
  /**
   * Show the working panel (affectUI Thinking State) instead of the thinking
   * trace, the status line and the Steps list. Connie chat only for now; Tia
   * and Salma keep the trace (VH3_CONNECT #549 follow-up).
   */
  workingPanel?: boolean;
  /** Library Documents every turn of this chat has touched, for follow-up turns. */
  sessionReferences?: LibraryReference[];
  message: ChatMessage;
  sessionId?: string | null;
  onRatingChange?: (rating: CHAT_MESSAGE_RATING) => void;
  onProcessed?: () => void;
  onProcessCompleted?: () => void;
}

const assistantMarkdownClassName =
  'prose prose-sm prose-chat-sm md:prose-base dark:prose-invert prose-h2:!mt-0 prose-h2:!scroll-m-0 max-w-none min-w-0';

const AssistantMessage: FC<Props> = ({
  message,
  workingPanel = false,
  sessionReferences,
  onProcessed,
  showMessageActions = true,
  showFeedback = true,
  onProcessCompleted,
  sessionId,
  onRatingChange,
}) => {
  const [feedbackOpenSource, setFeedbackOpenSource] = useState<
    'like' | 'dislike' | null
  >(null);
  const messageRef = useRef<HTMLDivElement>(null);
  const isTouchDevice = useIsTouchDevice();
  const { isCopied, copyToClipboard } = useCopyToClipboard();
  const {
    isLiked,
    isDisliked,
    isRated,
    handleLikeClick,
    handleDislikeClick,
    submitFeedback,
  } = useMessageFeedback({
    messageId: message.id,
    sessionId,
    rating: message.rating,
    onRatingChange,
  });

  const isStreaming = Boolean(message.isStreaming || message.isLiveStream);
  const hasContent = Boolean(message.content?.trim());
  const showMessageMeta = !message.isLoader && !isStreaming;
  const completedTools = message.toolCallOutputs ?? [];
  const thinkingSteps = message.thinkingSteps ?? [];
  const progressStatus = message.progressStatus;
  const progressNotes = message.progressNotes ?? [];
  const traceSteps = progressStatus
    ? thinkingSteps.filter(step => step.kind !== 'status')
    : thinkingSteps;
  const showToolChips =
    !isStreaming &&
    (completedTools.length > 0 || Boolean(message.tools?.length));
  // From the first token of a Connie turn, so the trace never shows under it.
  const showWorkingPanel =
    workingPanel &&
    Boolean(isStreaming || progressStatus || progressNotes.length);
  const liveMarkdownImmediate = shouldRenderLiveMarkdown(message.content);
  const throttleLiveMarkdown = Boolean(
    message.isLiveStream && !liveMarkdownImmediate
  );
  const liveMarkdownContent = useThrottledContent(
    message.content,
    throttleLiveMarkdown
  );

  // Page Citations in a finished reply (VH3_CONNECT #549): a reply made of prose
  // and lists renders as Cited Answer; one with tables, code or other blocks keeps
  // Markdown with each citation as a page marker.
  const citations = useMemo(() => {
    if (isStreaming || !hasContent) return null;
    // A follow-up turn ("give me page citations") cites documents an earlier
    // turn read, so match against the whole session; count only this turn's reads.
    const own = libraryReferencesFor(message);
    const references = mergeReferences(own, sessionReferences ?? []);
    if (!references.some(ref => ref.name)) return null;
    const model = buildCitedAnswer(message.content, references, {
      pagesRead: own.reduce((total, ref) => total + ref.pagesRead.length, 0),
    });
    if (!model.sources.length) return null;
    return {
      references,
      model,
      asCard: canRenderAsCitedAnswer(message.content),
    };
  }, [isStreaming, hasContent, message, sessionReferences]);

  const openFeedback = (type: 'like' | 'dislike') => {
    if (isRated) return;
    if (type === 'like') {
      handleLikeClick();
    } else {
      handleDislikeClick();
    }
    setFeedbackOpenSource(type);
  };

  const closeFeedback = () => {
    setFeedbackOpenSource(null);
  };

  const handleCopy = () => {
    if (!messageRef.current) return;
    copyToClipboard(messageRef.current.innerText);
  };

  useEffect(() => {
    if (message?.isStreaming !== undefined && !message.isStreaming) {
      onProcessCompleted?.();
    }
  }, [message.isStreaming, onProcessCompleted]);

  return (
    <WorksheetPhotoProvider outputs={completedTools}>
      <Message
        className={cn('min-w-0 max-w-full justify-start')}
        data-message-id={message.id}
      >
        <div
          className={cn(
            'relative flex min-w-0 max-w-full flex-col gap-2 group/message',
            // The affectUI cards size themselves as CSS containers, so the
            // column must give them a width while either is on screen.
            (citations?.asCard || showWorkingPanel) && 'w-full'
          )}
        >
          {message.isLoader ? (
            <AgentThinkingOrb
              size='inline'
              status={message.streamStatus ?? 'Thinking…'}
              toolName={message.toolActivity?.toolName}
              className='mr-2 mt-2'
            />
          ) : hasContent ? (
            <div
              ref={messageRef}
              className={cn(
                'min-w-0 max-w-full rounded-xl p-2 text-fg-default',
                citations?.asCard && 'w-full'
              )}
            >
              {message.isLiveStream ? (
                <Markdown
                  revealStreaming
                  className={assistantMarkdownClassName}
                >
                  {liveMarkdownImmediate
                    ? message.content
                    : liveMarkdownContent}
                </Markdown>
              ) : message.isStreaming ? (
                <LastBotMessage
                  markdownText={message.content}
                  onProcessed={message.isStreaming ? onProcessed : undefined}
                  onProcessCompleted={onProcessCompleted}
                />
              ) : citations?.asCard ? (
                <CitedAnswerMessage
                  content={message.content}
                  references={citations.references}
                  model={citations.model}
                  sessionId={sessionId}
                  turnIndex={message.turnIndex}
                />
              ) : (
                <Markdown className={assistantMarkdownClassName}>
                  {citations
                    ? linkLibraryCitations(
                        message.content,
                        citations.references
                      )
                    : message.content}
                </Markdown>
              )}
            </div>
          ) : null}

          {/* What Connie did while she worked: narration, thinking and Library
              searches as a timeline that folds to "Worked for Ns". It replaces
              the thinking trace, the status line and the Steps list, which the
              other agents keep. */}
          {showWorkingPanel ? (
            <ConnieWorkingPanel message={message} isLive={isStreaming} />
          ) : null}

          {!workingPanel && progressStatus ? (
            <p
              role='status'
              className='px-2 text-xs italic text-fg-muted'
              data-cy='agent-progress-status'
            >
              {progressStatus}
            </p>
          ) : null}

          {!workingPanel && progressNotes.length ? (
            <details
              className='px-2 text-xs text-fg-muted'
              data-cy='agent-progress-steps'
            >
              <summary className='cursor-pointer py-1'>Steps</summary>
              <ol className='mt-1 list-decimal space-y-0.5 pl-4'>
                {progressNotes.map((note, index) => (
                  <li key={`${index}-${note}`}>{note}</li>
                ))}
              </ol>
            </details>
          ) : null}

          {!showWorkingPanel &&
          traceSteps.length &&
          (isStreaming || !showToolChips) ? (
            <AgentThinkingTrace steps={traceSteps} isLive={isStreaming} />
          ) : null}

          {!isStreaming ? <WorksheetPhotoStrip /> : null}

          {showToolChips ? (
            <ToolCallChips
              outputs={completedTools}
              content={message.content}
              tools={message.tools}
              thinkingSteps={thinkingSteps}
            />
          ) : null}

          {showMessageMeta && (
            <div className='flex items-center gap-2.5 px-2'>
              <span className='text-xs text-fg-muted'>{message.timestamp}</span>
              {showMessageActions && (
                <MessageActions className='gap-2.5'>
                  <Tooltip label='Copy to clipboard' isDisabled={isTouchDevice}>
                    <IconButton
                      icon={
                        isCopied ? (
                          <CheckCheck size={16} className='text-green-400' />
                        ) : (
                          <Copy size={16} className='copy-icon' />
                        )
                      }
                      aria-label='Copy to clipboard'
                      variant='ghost'
                      minWidth={4}
                      height={4}
                      size='xs'
                      colorScheme='gray'
                      bg='transparent'
                      _hover={{ bg: 'transparent' }}
                      _active={{ bg: 'transparent' }}
                      sx={{
                        '.copy-icon': {
                          opacity: 0.6,
                          transitionProperty: 'color, opacity',
                          transitionDuration: '0.2s',
                        },
                        '&:hover .copy-icon': {
                          color: 'clipboard.icon-hover',
                        },
                      }}
                      onClick={handleCopy}
                    />
                  </Tooltip>
                  {showFeedback && (
                    <>
                      <Tooltip label='Helpful'>
                        <MessageFeedbackPopover
                          isOpen={feedbackOpenSource === 'like'}
                          onClose={closeFeedback}
                          onSubmitFeedback={submitFeedback}
                        >
                          <IconButton
                            icon={
                              <ThumbsUp
                                className={cn('size-4', {
                                  'text-green-400': isLiked,
                                })}
                              />
                            }
                            aria-label='Helpful'
                            variant='ghost'
                            minWidth={4}
                            height={4}
                            size='xs'
                            colorScheme='gray'
                            className={cn(
                              'opacity-60 hover:opacity-100',
                              isRated && 'pointer-events-none'
                            )}
                            onClick={() => openFeedback('like')}
                          />
                        </MessageFeedbackPopover>
                      </Tooltip>
                      <Tooltip label='Not helpful'>
                        <MessageFeedbackPopover
                          isOpen={feedbackOpenSource === 'dislike'}
                          onClose={closeFeedback}
                          onSubmitFeedback={submitFeedback}
                        >
                          <IconButton
                            icon={
                              <ThumbsDown
                                className={cn('size-4', {
                                  'text-red-400': isDisliked,
                                })}
                              />
                            }
                            aria-label='Not helpful'
                            variant='ghost'
                            minWidth={4}
                            height={4}
                            size='xs'
                            colorScheme='gray'
                            className={cn(
                              'opacity-60 hover:opacity-100',
                              isRated && 'pointer-events-none'
                            )}
                            onClick={() => openFeedback('dislike')}
                          />
                        </MessageFeedbackPopover>
                      </Tooltip>
                    </>
                  )}
                </MessageActions>
              )}
            </div>
          )}
        </div>
      </Message>
    </WorksheetPhotoProvider>
  );
};

export { AssistantMessage };
